"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.opBillEditRecordSchema = exports.opBillEditPendingResponseSchema = exports.opBillEditRequestSchema = exports.opBillEditPreviewResponseSchema = exports.opBillEditChangeSchema = void 0;
const zod_1 = require("zod");
const financial_correction_stage_response_1 = require("./financial-correction-stage.response");
const financial_payment_correction_request_1 = require("../request/financial-payment-correction.request");
const financial_op_bill_edit_request_1 = require("../request/financial-op-bill-edit.request");
/** One field of the change, as the approver reads it: names, never ids. `from` / `to` are display text ("—" for empty). */
exports.opBillEditChangeSchema = zod_1.z.object({ field: zod_1.z.string(), label: zod_1.z.string(), from: zod_1.z.string(), to: zod_1.z.string() });
/** "Would this edit be allowed, and what would it do?" - run before the clerk writes a reason, and again at request, verification and approval. */
exports.opBillEditPreviewResponseSchema = zod_1.z.object({
    eligible: zod_1.z.boolean(),
    blockers: zod_1.z.array(zod_1.z.string()),
    kind: financial_op_bill_edit_request_1.opBillEditTargetKindEnum,
    receiptNumber: zod_1.z.string(),
    patientName: zod_1.z.string(),
    changes: zod_1.z.array(exports.opBillEditChangeSchema),
    effects: zod_1.z.array(zod_1.z.string()),
});
exports.opBillEditRequestSchema = zod_1.z.object({
    id: zod_1.z.string().uuid(),
    status: financial_payment_correction_request_1.paymentCorrectionStatusEnum,
    kind: financial_op_bill_edit_request_1.opBillEditTargetKindEnum,
    invoiceId: zod_1.z.string().uuid().nullable(),
    depositId: zod_1.z.string().uuid().nullable(),
    receiptNumber: zod_1.z.string(),
    patientUhid: zod_1.z.string().nullable(),
    patientName: zod_1.z.string(),
    changes: zod_1.z.array(exports.opBillEditChangeSchema),
    reason: zod_1.z.string(),
    requestedBy: zod_1.z.string().uuid().nullable(),
    requestedByName: zod_1.z.string().nullable(),
    createdAt: zod_1.z.string(),
    stage: financial_correction_stage_response_1.correctionStageEnum,
    verifiedBy: zod_1.z.string().uuid().nullable(),
    verifiedByName: zod_1.z.string().nullable(),
    verifiedAt: zod_1.z.string().nullable(),
    verificationNote: zod_1.z.string().nullable(),
    approvedBy: zod_1.z.string().uuid().nullable(),
    approvedByName: zod_1.z.string().nullable(),
    approvedAt: zod_1.z.string().nullable(),
    decisionReason: zod_1.z.string().nullable(),
    appliedSummary: zod_1.z.array(zod_1.z.string()).nullable(),
    /** What approving would do TODAY, for a PENDING request - or why it can no longer be applied. Null once decided. */
    effects: zod_1.z.array(zod_1.z.string()).nullable().optional(),
});
exports.opBillEditPendingResponseSchema = zod_1.z.object({
    pending: zod_1.z.boolean(),
    requestedByName: zod_1.z.string().nullable(),
    createdAt: zod_1.z.string().nullable(),
});
const lineSchema = zod_1.z.object({
    id: zod_1.z.string().uuid(),
    /** 1-based position on the receipt. */
    serial: zod_1.z.number().int(),
    description: zod_1.z.string(),
    remarks: zod_1.z.string().nullable(),
    /** A USER id (what `DoctorDto.userId` carries), or null. */
    doctorId: zod_1.z.string().uuid().nullable(),
    doctorName: zod_1.z.string().nullable(),
    /** The consultation fee line: its doctor changes through Doctor transfer (it moves the booking with it). */
    isConsultation: zod_1.z.boolean(),
    /** Why this line's doctor cannot be changed here, or null. */
    doctorLock: zod_1.z.string().nullable(),
    /** The line's figures as billed (slices 3-4). */
    quantity: zod_1.z.number(),
    unitRatePaise: zod_1.z.number().int().nullable(),
    grossPaise: zod_1.z.number().int(),
    discountPaise: zod_1.z.number().int(),
    discountPercent: zod_1.z.number().nullable(),
    taxPaise: zod_1.z.number().int(),
    netPaise: zod_1.z.number().int(),
    /** Whether this line's rate was typed at the counter (an open-value service), so a correction may change it. */
    rateEditable: zod_1.z.boolean(),
    /** Why this line cannot be removed or have its quantity / rate changed (lab work raised from it, the consultation ...), or null. */
    lineLock: zod_1.z.string().nullable(),
});
const paymentLineSchema = zod_1.z.object({
    id: zod_1.z.string().uuid(),
    amountPaise: zod_1.z.number().int(),
    paymentModeId: zod_1.z.string().uuid(),
    modeCode: zod_1.z.string(),
    modeName: zod_1.z.string(),
    bankAccountId: zod_1.z.string().uuid().nullable(),
    bankName: zod_1.z.string().nullable(),
    referenceNumber: zod_1.z.string().nullable(),
    /** Why this line cannot be changed (cash in a drawer already closed), or null. */
    lock: zod_1.z.string().nullable(),
});
const paymentSchema = zod_1.z.object({
    paymentId: zod_1.z.string().uuid(),
    paymentNumber: zod_1.z.string(),
    receiptNumber: zod_1.z.string().nullable(),
    /** Why this payment's lines cannot be changed here (its shift is closed, it was refunded ...), or null. */
    lock: zod_1.z.string().nullable(),
    lines: zod_1.z.array(paymentLineSchema),
});
/**
 * THE RECEIPT AS THE EDIT FORM NEEDS IT: every editable value as it is on file, whether the caller may ask at all, and - group by group - what
 * is locked and why, so the dialog disables a control with the reason instead of letting the clerk find out at Check.
 */
exports.opBillEditRecordSchema = zod_1.z.object({
    kind: financial_op_bill_edit_request_1.opBillEditTargetKindEnum,
    invoiceId: zod_1.z.string().uuid().nullable(),
    depositId: zod_1.z.string().uuid().nullable(),
    receiptNumber: zod_1.z.string(),
    patientName: zod_1.z.string(),
    patientUhid: zod_1.z.string().nullable(),
    /** `FinInvoice.version` / `IpAdmissionDeposit.lockVersion` - sent back with the request. */
    version: zod_1.z.number().int(),
    status: zod_1.z.string(),
    netAmountPaise: zod_1.z.number().int(),
    /** The caller may ask for an edit of this receipt. When false, `blockers` says why (not their receipt, cancelled ...). */
    canRequest: zod_1.z.boolean(),
    blockers: zod_1.z.array(zod_1.z.string()),
    /** Group -> why it cannot be changed. Keys: walkIn, newborn, creditParty, lineDoctors, lineRemarks, paymentLines, billDiscounts, serviceLines, paymentKind, notes. */
    locks: zod_1.z.record(zod_1.z.string(), zod_1.z.string()),
    /** OP receipt only. */
    walkIn: zod_1.z.object({ isWalkIn: zod_1.z.boolean(), name: zod_1.z.string().nullable(), mobile: zod_1.z.string().nullable(), ageYears: zod_1.z.number().int().nullable(), gender: zod_1.z.string().nullable() }).nullable(),
    newborn: zod_1.z.object({ name: zod_1.z.string(), ageDays: zod_1.z.number().int(), gender: zod_1.z.string() }).nullable(),
    creditParty: zod_1.z
        .object({ partyType: zod_1.z.enum(['EMPLOYEE', 'COMPANY']), employeeId: zod_1.z.string().uuid().nullable(), payerId: zod_1.z.string().uuid().nullable(), partyName: zod_1.z.string() })
        .nullable(),
    lines: zod_1.z.array(lineSchema),
    /** OP receipt only (slices 3-4): the bill's totals as billed. */
    bill: zod_1.z
        .object({
        grossPaise: zod_1.z.number().int(),
        /** Line discounts plus the bill-level one. */
        discountPaise: zod_1.z.number().int(),
        lineDiscountPaise: zod_1.z.number().int(),
        billDiscountPaise: zod_1.z.number().int(),
        taxPaise: zod_1.z.number().int(),
        roundOffPaise: zod_1.z.number().int(),
        netPaise: zod_1.z.number().int(),
    })
        .nullable(),
    /** OP receipt only (slice 2): the money taken against this bill, payment by payment. */
    payments: zod_1.z.array(paymentSchema),
    paidPaise: zod_1.z.number().int(),
    /** IP advance only. */
    paymentKind: zod_1.z.string().nullable(),
    notes: zod_1.z.string().nullable(),
});
//# sourceMappingURL=financial-op-bill-edit.response.js.map